import { ChangeEvent, FC, useState } from 'react';
import { Stack } from '@chakra-ui/react';

import zodLocales from 'common/zod/locales';
import * as Form from 'components/_core/form';
import SunshineModal from 'components/_core/SunshineModal';
import CountryPicker from 'components/CountryPicker';
import { isValidEmail } from 'components/LegacyInput/utils';
import { StepProps } from 'components/Stepper/types';

import ClientFormWrapper from '../../components/ClientFormWrapper';
import {
  ClientContactForm,
  createEmptyClientContact,
  useClientFormModalContext,
} from '../../hooks/useClientFormModalContext';
import locales from './locales';

interface ClientFormProps extends StepProps {
  shouldAutoFill: boolean;
}

const ClientForm: FC<ClientFormProps> = ({ goNext, shouldAutoFill }) => {
  const { client } = useClientFormModalContext();

  const clientContact =
    shouldAutoFill && client?.contacts.length && client?.clientId
      ? (client.contacts[0] as ClientContactForm)
      : createEmptyClientContact();

  const [clientInput, setClientInput] = useState({
    ...client,
    ...clientContact,
  });

  const { city, country, email, firstName, lastName, phone, street, zip } =
    clientInput;

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setClientInput({
      ...clientInput,
      [e.target.name]: e.target.value,
    });
  };

  const canSaveClient =
    !!firstName.trim() && !!lastName.trim() && isValidEmail(email);

  return (
    <>
      <SunshineModal.Header />

      <ClientFormWrapper
        canSubmit={canSaveClient}
        clientInput={clientInput}
        showAlert={() => goNext({ key: 'client-save-changes-alert' })}
      >
        <Stack spacing="space-24">
          <Form.Field label={locales.firstName}>
            <Form.Input
              defaultValue={firstName}
              name="firstName"
              onChange={onChange}
              placeholder={locales.firstName}
              required
              type="text"
            />
          </Form.Field>

          <Form.Field label={locales.lastName}>
            <Form.Input
              defaultValue={lastName}
              name="lastName"
              onChange={onChange}
              placeholder={locales.lastName}
              required
              type="text"
            />
          </Form.Field>

          <Form.Field
            error={
              !isValidEmail(email) && email !== ''
                ? zodLocales.errors.invalidEmail
                : undefined
            }
            label={locales.email}
          >
            <Form.Input
              name="email"
              onChange={onChange}
              placeholder={locales.email}
              required
              type="email"
              value={email}
            />
          </Form.Field>

          <Form.Field label={locales.phone}>
            <Form.Input
              name="phone"
              onChange={onChange}
              placeholder={locales.phone}
              type="text"
              value={phone}
            />
          </Form.Field>

          <Form.Field label={locales.street}>
            <Form.Input
              name="street"
              onChange={onChange}
              placeholder={locales.street}
              type="text"
              value={street}
            />
          </Form.Field>

          <Form.Field label={locales.zip}>
            <Form.Input
              name="zip"
              onChange={onChange}
              placeholder={locales.zip}
              type="text"
              value={zip}
            />
          </Form.Field>

          <Form.Field label={locales.city}>
            <Form.Input
              name="city"
              onChange={onChange}
              placeholder={locales.city}
              type="text"
              value={city}
            />
          </Form.Field>

          <CountryPicker
            label={locales.country}
            onSelect={(value) =>
              onChange({
                target: { name: 'country', value },
              } as unknown as ChangeEvent<HTMLInputElement>)
            }
            selected={country}
          />
        </Stack>
      </ClientFormWrapper>
    </>
  );
};

export default ClientForm;
